// apps/client/src/screens/Checkout/CheckoutForm.tsx
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Button,
  Checkbox,
  Collapse,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Heading,
  Input,
  Radio,
  RadioGroup,
  Spacer,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  Spinner,
  VStack,
  Box,
  Tooltip,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import InputMask from 'react-input-mask';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

import {
  useFetchClientCompleteInfo,
  useFetchCustomPaymentMethods,
  useFetchAvailablePaymentPlans,
  useListClientServices,
} from 'hooks/useApiQueries';
import { useUserContext } from 'providers/useUserContext';
import { useTranslation } from 'react-i18next';
import { Location, useLocation, useNavigate } from 'react-router';
import Auth from 'screens/Account/Auth';
import { ApiClientExceptionErrorCode } from 'types/api';
import { PromoCode } from 'types/promocode';
import { ApiClientException } from 'utils/apiClientException';
import { HTTP } from 'utils/http';
import { Product } from '.';
import * as Sentry from '@sentry/react';

export interface PaymentMethod {
  planDetails: Record<string, string | number>[];
  maxAmount: number;
  minAmount: number;
  installmentsCount: number;
  Id: number;
  Name: string;
}

export interface CheckoutData {
  AlmaPaymentStatus: string;
  InstallmentsCount: number;
  ServiceId: number;
  Amount: number;
  TotalAmount: number;
  DiscountedAmount: number;
  GiftCard: string;
  GiftCardBalance?: number;
  BillingName: string;
  BillingLastName: string;
  BillingAddress: string;
  BillingCity: string;
  BillingPostalCode: string;
  CreditCardNumber: string;
  Exp: string;
  Cryptogram: string;
  SaveInfo: boolean;
  StoredCreditCard: 'new' | 'saved' | 'gift-card';
  PromoCode: string;
  PaymentMethod: string;
  Email: string;
  Phone: string;
  // Nouveaux champs pour SCA
  Sca3DSecureRequired?: boolean;
  ScaExemptionRequested?: string;
  ScaAuthenticationUrl?: string;
  ScaTransactionId?: string;
  ScaReturnUrl?: string;
}

export interface CheckoutFormProps {
  amount: number;
  onPurchase: (data: CheckoutData) => Promise<Record<string, unknown>>; // Ensure this type is consistent
}

export function CheckoutForm({ amount, onPurchase }: CheckoutFormProps) {
  const { data: customPaymentMethods } = useFetchCustomPaymentMethods();
  const { data: paymentPlans, isLoading: isLoadingPaymentPlans } = useFetchAvailablePaymentPlans(amount);
  const [paymentMethod, setPaymentMethod] = useState('Credit Card');
  const [selectedPlan, setSelectedPlan] = useState<number | null>(null);
  const [isRefetchingPaymentPlans, setIsRefetchingPaymentPlans] = useState(false);
  const [paymentInProgress, setPaymentInProgress] = useState(false);
  const [showFallbackRedirect, setShowFallbackRedirect] = useState(false);
  const fallbackTimerRef = useRef<NodeJS.Timeout | null>(null);
  const forceRedirectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const { t } = useTranslation();
  const { isLoggedIn, user } = useUserContext();
  const toast = useToast();
  const { data: clientCompleteInfo } = useFetchClientCompleteInfo();
  const { data: clientServices } = useListClientServices() || {};
  const { state } = useLocation() || {};
  const { from, product } = state as { from?: Location; product: Product };

  const hasAlreadyBoughtWelcomePack = clientServices?.some((service) =>
    service.Name.toLowerCase().includes('bienvenue'),
  );
  const disableConfirmAlmaPayment = product?.IsIntroOffer && hasAlreadyBoughtWelcomePack;

  const navigate = useNavigate();

  const client = clientCompleteInfo?.Client;
  const clientCreditCard = client?.ClientCreditCard;
  const clientHasStoredCreditCard = !!clientCreditCard?.CardNumber;

  const [updatedPaymentPlans, setUpdatedPaymentPlans] = useState<PaymentMethod[] | null>(null);

  const isAlmaAvailable =
    mondayMindbody.siteId === '5736526' || // Dynamo Paris
    mondayMindbody.siteId === '5736555' || // Riise Paris
    mondayMindbody.siteId === '5736551' // Punch Paris
      ? true
      : false;

  useEffect(() => {
    if (paymentMethod === 'Alma' && paymentPlans) {
      const defaultPlan = paymentPlans.find((plan) => plan.installmentsCount === 3);
      if (defaultPlan) {
        setSelectedPlan(defaultPlan.installmentsCount);
      }
    }
  }, [paymentMethod, paymentPlans]);

  const handlePaymentChange = (value: string) => {
    setPaymentMethod(value);
    setSelectedPlan(null); // Reset selected plan when payment method changes
  };

  const handlePlanSelect = (planIndex: number) => {
    setSelectedPlan(planIndex);
  };

  const getDefaultValues = (): CheckoutData => {
    return {
      Amount: amount,
      TotalAmount: amount,
      DiscountedAmount: 0,
      PromoCode: '',
      GiftCard: '',
      BillingName: clientCreditCard?.CardHolder ?? client?.LastName ?? '',
      BillingLastName: client?.LastName ?? '',
      BillingAddress: clientCreditCard?.Address ?? client?.AddressLine1 ?? '',
      BillingCity: clientCreditCard?.City ?? client?.City ?? '',
      BillingPostalCode: clientCreditCard?.PostalCode ?? client?.PostalCode ?? '',
      CreditCardNumber: '',
      Exp: '',
      Cryptogram: '',
      SaveInfo: false,
      StoredCreditCard: clientHasStoredCreditCard ? 'saved' : 'new',
      PaymentMethod: 'Credit Card',
      InstallmentsCount: 0,
      Email: user?.Email ?? '',
      Phone: `00${user?.MobilePhone}`,
      AlmaPaymentStatus: '',
      ServiceId: 0,
    };
  };

  const form = useForm<CheckoutData>({
    defaultValues: getDefaultValues(),
  });

  const {
    setValue,
    getValues,
    register,
    handleSubmit,
    watch,
    unregister,
    control,
    formState: { errors, isSubmitting, isValid },
  } = form;

  const isUsingSavedCreditCard = watch('StoredCreditCard') === 'saved';
  const storedCreditCard = watch('StoredCreditCard');
  const creditCardNumber = watch('CreditCardNumber');
  const cryptogram = watch('Cryptogram');
  const discount = watch('DiscountedAmount');
  const giftCardValue = watch('GiftCardBalance');
  const totalAmount = watch('TotalAmount') ?? 0;
  const productAmount = watch('Amount');

  const effectiveGiftCardValue = giftCardValue ?? 0;
  const remainingAmount = Math.max(totalAmount - effectiveGiftCardValue, 0);
  const vatAmount = remainingAmount * 0.2;

  useEffect(() => {
    if (paymentMethod === 'Alma' && remainingAmount > 0) {
      setIsRefetchingPaymentPlans(true);
      const fetchUpdatedPaymentPlans = async () => {
        try {
          const plans = await HTTP.get<PaymentMethod[]>(`/sale/almapaymentplans`, { remainingAmount }).send();
          if (plans) {
            setUpdatedPaymentPlans(plans);
            const defaultPlan = plans.find((plan) => plan.installmentsCount === 3);
            if (defaultPlan) {
              setSelectedPlan(defaultPlan.installmentsCount);
            }
          }
        } catch (error) {
          console.error('Error fetching updated payment plans:', error);
        }
      };
      setIsRefetchingPaymentPlans(false);

      fetchUpdatedPaymentPlans();
    }
  }, [paymentMethod, remainingAmount]);

  const giftCardValueCoversTotalAmount = giftCardValue && giftCardValue >= totalAmount;

  // Fonction pour vérifier si le numéro de carte est une AMEX
  const isAmex = (cardNumber: string) => {
    const amexPattern = /^3[47][0-9]{13}$/;
    return amexPattern.test(cardNumber.replace(/\s/g, ''));
  };

  useEffect(() => {
    // Register fields with conditions based on the payment method and remaining amount
    register('BillingName', {
      required:
        remainingAmount > 0 &&
        (paymentMethod === 'Alma' || (!isUsingSavedCreditCard && paymentMethod === 'Credit Card')),
    });
    register('BillingLastName', { required: remainingAmount > 0 && paymentMethod === 'Alma' });
    register('Email', { required: remainingAmount > 0 && paymentMethod === 'Alma' });
    register('BillingAddress', {
      required:
        remainingAmount > 0 &&
        (paymentMethod === 'Alma' || (!isUsingSavedCreditCard && paymentMethod === 'Credit Card')),
    });
    register('BillingCity', {
      required:
        remainingAmount > 0 &&
        (paymentMethod === 'Alma' || (!isUsingSavedCreditCard && paymentMethod === 'Credit Card')),
    });
    register('BillingPostalCode', {
      required:
        remainingAmount > 0 &&
        (paymentMethod === 'Alma' || (!isUsingSavedCreditCard && paymentMethod === 'Credit Card')),
    });

    // Register CreditCardNumber only if payment is required and using Credit Card
    register('CreditCardNumber', {
      required: remainingAmount > 0 && paymentMethod === 'Credit Card' && !isUsingSavedCreditCard,
      validate: (value) => {
        if (remainingAmount === 0 || paymentMethod !== 'Credit Card' || isUsingSavedCreditCard) return true;
        const cleanedValue = value.replace(/\s/g, '');
        if (isAmex(cleanedValue)) {
          return cleanedValue.length === 15 || 'Numéro de carte AMEX invalide';
        }
        return cleanedValue.length === 16 || 'Numéro de carte invalide';
      },
    });

    // Register Exp only if payment is required and using Credit Card
    register('Exp', {
      required: remainingAmount > 0 && paymentMethod === 'Credit Card' && !isUsingSavedCreditCard,
    });

    // Register Cryptogram only if payment is required and using Credit Card
    register('Cryptogram', {
      required: remainingAmount > 0 && paymentMethod === 'Credit Card' && !isUsingSavedCreditCard,
      pattern: isAmex(creditCardNumber) ? /^[0-9]{4}$/ : /^[0-9]{3}$/,
    });

    register('SaveInfo');
  }, [isUsingSavedCreditCard, creditCardNumber, unregister, register, paymentMethod, remainingAmount]);

  // Cleanup des timers au démontage du composant
  useEffect(() => {
    return () => {
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current);
      }
      if (forceRedirectTimerRef.current) {
        clearTimeout(forceRedirectTimerRef.current);
      }
    };
  }, []);

  async function onSubmit(data: CheckoutData) {
    if (paymentInProgress) {
      console.log('Payment already in progress, ignoring click');
      return;
    }

    setPaymentInProgress(true);

    try {
      console.log('onSubmit called', { data: JSON.stringify(data), remainingAmount });

      // If the total amount is 0, use Comp payment method
      if (remainingAmount <= 0) {
        await onPurchase(data);

        toast({
          status: 'success',
          title: 'Confirmation réussie !',
          description: 'Votre crédit a été ajouté à votre compte.',
          duration: 3000,
          isClosable: true,
        });

        if (from) {
          if (from.pathname === '/tarifs') {
            return navigate('/account/services', { replace: true });
          }
          return navigate(-1);
        }
        return navigate('/', { replace: true });
      }

      if (data.StoredCreditCard === 'new') {
        data.CreditCardNumber = data.CreditCardNumber.replaceAll(/\s/g, '');
      }

      data.PaymentMethod = paymentMethod;

      // Traitement spécial pour les paiements Alma (déjà sécurisés)
      if (paymentMethod === 'Alma' && selectedPlan !== null) {
        data.InstallmentsCount = selectedPlan;
        data.ServiceId = product.Id as number;

        Sentry.withScope((scope) => {
          scope.setTag('payment_method', 'alma');
          scope.setTag('payment_status', 'initiated');
          scope.setLevel('info');
          scope.setContext('payment_details', {
            paymentMethod,
            selectedPlan,
            amount: data.Amount,
            totalAmount: data.TotalAmount,
            discountedAmount: data.DiscountedAmount,
            serviceId: data.ServiceId,
            user: {
              name: `${user?.FirstName} ${user?.LastName}`,
              email: user?.Email,
            },
            product: JSON.stringify(product),
          });
          Sentry.captureMessage('Alma payment initiated', {
            level: 'info',
            tags: {
              category: 'payment',
              paymentMethod: 'alma',
            },
            extra: {
              paymentMethod,
              selectedPlan,
              user: `${user?.FirstName} ${user?.LastName}`,
              email: user?.Email,
            },
          });
        });

        const response: Record<string, unknown> = await onPurchase(data);

        if (response) {
          data.AlmaPaymentStatus = 'pending';
          sessionStorage.setItem('paymentData', JSON.stringify(data));

          Sentry.startSession();

          Sentry.captureMessage('Alma payment redirect', {
            level: 'info',
            tags: {
              category: 'payment',
              paymentMethod: 'alma',
            },
            extra: {
              paymentMethod,
              selectedPlan,
              user: `${user?.FirstName} ${user?.LastName}`,
              email: user?.Email,
              response,
              redirectUrl: response.url,
            },
          });

          return (window.location.href = response?.url as string);
        }
      }

      // Traitement des paiements par carte de crédit avec SCA
      if (paymentMethod === 'Credit Card') {
        // Configuration de l'URL de retour pour SCA
        const currentUrl = window.location.href;
        const baseUrl = currentUrl.split('#')[0];
        data.ScaReturnUrl = `${baseUrl}#/sca-callback`;

        console.log('Processing credit card payment with SCA support', {
          paymentMethod,
          storedCreditCard: data.StoredCreditCard,
          amount: data.Amount,
          scaReturnUrl: data.ScaReturnUrl,
        });

        const response: Record<string, unknown> = await onPurchase(data);

        // Vérifier si une authentification SCA est requise
        if (response && response.sca3DSecureRequired) {
          console.log('SCA 3D Secure authentication required, redirecting...', {
            scaTransactionIds: response.scaTransactionIds,
            scaAuthenticationUrls: response.scaAuthenticationUrls,
          });

          // Sauvegarder les données pour le second appel après authentification
          const scaData = {
            ...data,
            ServiceId: product.Id as number,
            TransactionIDs: response.scaTransactionIds,
            originalResponse: response.originalResponse,
          };
          sessionStorage.setItem('scaPaymentData', JSON.stringify(scaData));

          // Redirection vers la première URL d'authentification
          const authUrls = response.scaAuthenticationUrls as string[];
          if (authUrls && authUrls.length > 0) {
            window.location.href = authUrls[0];
            return;
          }
        }

        // Paiement réussi sans SCA
        await handleSuccessfulPayment();
        return;
      }

      // Fallback pour autres méthodes de paiement
      await onPurchase(data);
      await handleSuccessfulPayment();
    } catch (e) {
      Sentry.withScope((scope) => {
        scope.setTag('where', 'CheckoutForm:onSubmit');
        scope.setExtra('paymentMethod', paymentMethod);
        scope.setExtra('selectedPlan', selectedPlan);
        scope.setExtra('checkoutData', data);
        scope.setExtra('payment_details', {
          selectedPlan,
          amount: data.Amount,
          totalAmount: data.TotalAmount,
          discountedAmount: data.DiscountedAmount,
          serviceId: data.ServiceId,
          isUsingSavedCreditCard: isUsingSavedCreditCard,
          giftCardValue: giftCardValue,
          hasPromoCode: !!data.PromoCode,
          product: JSON.stringify(product),
          scaSupport: {
            exemptionRequested: data.ScaExemptionRequested,
            authenticationRequired: data.Sca3DSecureRequired,
            transactionId: data.ScaTransactionId,
          },
        });

        if (paymentMethod === 'Alma' && selectedPlan !== null) {
          scope.setExtra('almaSpecificData', {
            InstallmentsCount: data.InstallmentsCount,
            ServiceId: data.ServiceId,
          });
        }
        if (e instanceof SyntaxError && e.message.includes('JSON.parse')) {
          scope.setTag('error_type', 'json_parse_error');
          scope.setLevel('error');
          Sentry.captureException(e, {
            extra: {
              details: 'Erreur lors du parsing de la réponse du serveur',
              originalError: e,
            },
          });
        } else if (e instanceof ApiClientException) {
          scope.setTag('error_type', 'api_client_error');
          scope.setExtra('api_error_details', {
            errorCode: e.ErrorCode,
            errors: e.errors,
            message: e.getMessage(),
          });
          Sentry.captureException(e);
        } else {
          scope.setTag('error_type', 'unknown_error');
          Sentry.captureException(e);
        }
      });

      if (e instanceof ApiClientException) {
        const title = e.getMessage();
        const errors = e.errors || [];

        if (errors.length === 0) {
          toast({
            status: 'error',
            title: title,
          });
        } else {
          errors.forEach((error: { message: string }) => {
            toast({
              status: 'error',
              title: error.message || title,
            });
          });
        }

        if (e.ErrorCode === ApiClientExceptionErrorCode.GiftCardBalanceMismatch) {
          setValue('GiftCardBalance', undefined);
          setValue('GiftCard', '');
        }
      } else {
        toast({
          status: 'error',
          title: 'Erreur lors du paiement',
          description: 'Une erreur inattendue est survenue. Veuillez réessayer ou contacter le support.',
        });
      }
    } finally {
      setPaymentInProgress(false);
    }
  }

  // Fonction pour gérer un paiement réussi
  const handleSuccessfulPayment = async () => {
    toast({
      status: 'success',
      title: 'Paiement réussi !',
      description: 'Votre crédit a été ajouté à votre compte. Redirection en cours...',
      duration: 5000,
      isClosable: true,
    });

    console.log('Payment completed successfully, initiating redirect', {
      paymentMethod,
      from: from?.pathname,
      timestamp: Date.now(),
    });

    // Pour les paiements CB, démarrer les timers après le paiement réussi
    if (paymentMethod === 'Credit Card') {
      fallbackTimerRef.current = setTimeout(() => {
        console.log('Showing fallback redirect button');
        Sentry.captureMessage('Fallback redirect button shown', {
          level: 'warning',
          tags: {
            category: 'payment_redirect',
            issue: 'redirect_delay',
          },
          extra: {
            paymentMethod: 'Credit Card',
            userEmail: user?.Email,
            from: from?.pathname,
          },
        });
        setShowFallbackRedirect(true);
      }, 10000);

      forceRedirectTimerRef.current = setTimeout(() => {
        console.log('Force redirecting to account services');
        Sentry.captureMessage('Force redirect triggered', {
          level: 'error',
          tags: {
            category: 'payment_redirect',
            issue: 'force_redirect_needed',
          },
          extra: {
            paymentMethod: 'Credit Card',
            userEmail: user?.Email,
            from: from?.pathname,
          },
        });
        navigate('/account/services', { replace: true });
      }, 15000);
    }

    const cleanupTimers = () => {
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
      if (forceRedirectTimerRef.current) {
        clearTimeout(forceRedirectTimerRef.current);
        forceRedirectTimerRef.current = null;
      }
    };

    const redirectToAccount = () => {
      console.log('Executing immediate redirect to account services');
      cleanupTimers();
      navigate('/account/services', { replace: true });
    };

    if (from) {
      if (from.pathname === '/tarifs') {
        return redirectToAccount();
      }
      console.log('Navigating back with navigate(-1)');
      cleanupTimers();
      return navigate(-1);
    }

    console.log('Navigating to home page');
    cleanupTimers();
    navigate('/', { replace: true });
  };

  const handleFallbackRedirect = () => {
    console.log('Manual fallback redirect triggered');

    // Nettoyer les timers puisque l'utilisateur a cliqué manuellement
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
    if (forceRedirectTimerRef.current) {
      clearTimeout(forceRedirectTimerRef.current);
      forceRedirectTimerRef.current = null;
    }

    navigate('/account/services', { replace: true });
  };

  if (!isLoggedIn) {
    return <Auth />;
  }

  const submitEnabled =
    isUsingSavedCreditCard ||
    giftCardValueCoversTotalAmount ||
    isValid ||
    (paymentMethod === 'Alma' &&
      getValues('Email') &&
      getValues('Phone') &&
      getValues('BillingName') &&
      getValues('BillingLastName') &&
      getValues('BillingAddress') &&
      getValues('BillingCity') &&
      getValues('BillingPostalCode')) ||
    (isAmex(creditCardNumber) && cryptogram.length === 4);

  const getProductPrice = () => {
    if (product.type === 'giftcard') {
      if (product.SalePrice === 0) {
        return getValues('Amount');
      }

      return product.SalePrice.toFixed(2);
    }

    return product.Price.toFixed(2);
  };

  // Add a function to check if Alma is selectable
  const isAlmaSelectable = remainingAmount >= 100;

  return (
    <FormProvider {...form}>
      <Stack spacing={5} direction={{ base: 'column', md: 'row' }} className="checkoutForm">
        <Table borderRadius="5px" width={{ base: '100%', md: 'auto' }} size="sm">
          <Thead background="#D9D9D9">
            <Tr borderTopLeftRadius="5px" borderTopRightRadius="5px">
              <Th>{t('Crédit')}</Th>
              <Th textAlign="center">{t('Qté')}</Th>
              <Th textAlign="center">{t('Prix')}</Th>
              {!discount && <Th textAlign="center"></Th>}
              {discount > 0 && <Th textAlign="center">{t('Remise')}</Th>}
              <Th textAlign="center">{t('Sous-total')}</Th>
            </Tr>
          </Thead>
          <Tbody background="#F5F5F5">
            <Tr>
              <Td>{product.type === 'giftcard' ? product.Description : product.Name}</Td>
              <Td textAlign="center">1</Td>
              <Td textAlign="center">{getProductPrice()} €</Td>
              {!discount && <Td></Td>}
              {discount > 0 && <Td textAlign="center">-{discount.toFixed(2)} €</Td>}
              <Td textAlign="center">{productAmount.toFixed(2)} €</Td>
            </Tr>
            {giftCardValue && (
              <Tr>
                <Td colSpan={4}>{t('Paiement carte cadeau')}</Td>
                <Td textAlign="center">-{(giftCardValue >= totalAmount ? totalAmount : giftCardValue).toFixed(2)} €</Td>
              </Tr>
            )}
            <Tr>
              <Td colSpan={4} fontWeight="bold" textAlign="right">
                {t('TVA incluse dans le total')}
              </Td>
              <Td textAlign="center">{vatAmount.toFixed(2)} €</Td>
            </Tr>
            <Tr borderBottomLeftRadius="5px" borderBottomRightRadius="5px" borderBottom="0px solid transparent">
              <Td colSpan={3}></Td>
              <Td fontWeight="bold" textAlign="center">
                {t('TOTAL')}
              </Td>
              <Td textAlign="center">{remainingAmount.toFixed(2)} €</Td>
            </Tr>
          </Tbody>
        </Table>
      </Stack>
      <Stack as="form" onSubmit={handleSubmit(onSubmit)} className="checkoutForm">
        {product.type === 'service' && (
          <Accordion allowToggle mb={5}>
            {mondayMindbody.siteId === '5736551' && product.ProgramId === 25 && (
              <>
                {Number(product.Id) !== 113131 && (
                  <AccordionItem className="accordionGiftCard">
                    <AccordionButton>
                      <HStack w="full" justifyContent="space-between">
                        <Text>{t('Carte cadeau')}</Text>
                        <AccordionIcon />
                      </HStack>
                    </AccordionButton>
                    <AccordionPanel>
                      <CheckoutGiftCard />
                    </AccordionPanel>
                  </AccordionItem>
                )}

                <AccordionItem className="accordionPromoCode">
                  <AccordionButton>
                    <HStack w="full" justifyContent="space-between">
                      <Text>{t('Code Promo')}</Text>
                      <AccordionIcon />
                    </HStack>
                  </AccordionButton>
                  <AccordionPanel>
                    <CheckoutPromoCode product={product} />
                  </AccordionPanel>
                </AccordionItem>
              </>
            )}
            {mondayMindbody.siteId === '5736551' && product.ProgramId !== 25 && Number(product.Id) !== 113131 && (
              <AccordionItem className="accordionPromoCode">
                <AccordionButton>
                  <HStack w="full" justifyContent="space-between">
                    <Text>{t('Code Promo')}</Text>
                    <AccordionIcon />
                  </HStack>
                </AccordionButton>
                <AccordionPanel>
                  <CheckoutPromoCode product={product} />
                </AccordionPanel>
              </AccordionItem>
            )}
            {mondayMindbody.siteId !== '5736551' && (
              <>
                {Number(product.Id) !== 113131 && (
                  <AccordionItem className="accordionGiftCard">
                    <AccordionButton>
                      <HStack w="full" justifyContent="space-between">
                        <Text>{t('Carte cadeau')}</Text>
                        <AccordionIcon />
                      </HStack>
                    </AccordionButton>
                    <AccordionPanel>
                      <CheckoutGiftCard />
                    </AccordionPanel>
                  </AccordionItem>
                )}

                <AccordionItem className="accordionPromoCode">
                  <AccordionButton>
                    <HStack w="full" justifyContent="space-between">
                      <Text>{t('Code Promo')}</Text>
                      <AccordionIcon />
                    </HStack>
                  </AccordionButton>
                  <AccordionPanel>
                    <CheckoutPromoCode product={product} />
                  </AccordionPanel>
                </AccordionItem>
              </>
            )}
          </Accordion>
        )}

        {/* Only show payment methods if there's a remaining amount to pay */}
        {remainingAmount > 0 && (
          <>
            {!giftCardValueCoversTotalAmount && (
              <>
                {clientHasStoredCreditCard && (
                  <Controller
                    name="StoredCreditCard"
                    control={control}
                    render={({ field: { onChange, value } }) => {
                      return (
                        <RadioGroup onChange={onChange} value={value}>
                          <Stack>
                            <Radio value="saved">
                              {t('Utiliser la carte se terminant par {{ lastFour }}', {
                                lastFour: clientCompleteInfo?.Client.ClientCreditCard?.CardNumber,
                              })}
                            </Radio>
                            <Radio value="new">{t('Utiliser un nouveau moyen de paiement')}</Radio>
                          </Stack>
                        </RadioGroup>
                      );
                    }}
                  />
                )}

                <Collapse in={!isUsingSavedCreditCard} unmountOnExit>
                  {!isUsingSavedCreditCard && (
                    <Stack mt={5}>
                      <Heading textTransform="uppercase" size="md">
                        {t('Adresse de facturation')}
                      </Heading>
                      <FormControl>
                        <FormLabel>{t('Adresse')}</FormLabel>
                        <Input {...register('BillingAddress', { required: true })} />
                      </FormControl>
                      <HStack>
                        <FormControl flex={2}>
                          <FormLabel>{t('Ville')}</FormLabel>
                          <Input {...register('BillingCity', { required: true })} />
                        </FormControl>
                        <FormControl flex={1}>
                          <FormLabel>{t('Code postal')}</FormLabel>
                          <Input {...register('BillingPostalCode', { required: true })} />
                        </FormControl>
                      </HStack>

                      <Stack mt={10}>
                        <Heading textTransform="uppercase" size="md">
                          {t('Paiement')}
                        </Heading>
                        <Spacer />

                        <RadioGroup value={paymentMethod} onChange={handlePaymentChange}>
                          <Stack direction="column">
                            <HStack mb={2}>
                              <Radio value="Credit Card">Payer par carte</Radio>
                              <HStack spacing={2} ml={2}>
                                <img
                                  src="https://www.reussir-mon-ecommerce.fr/wp-content/uploads/2016/03/logo-cb-1-300x214.jpg"
                                  alt="CB"
                                  style={{ height: '30px', width: 'auto' }}
                                />
                                <img
                                  src="https://www.reussir-mon-ecommerce.fr/wp-content/uploads/2016/03/MasterCard-Logo-1-300x225.png"
                                  alt="Mastercard"
                                  style={{ height: '30px', width: 'auto' }}
                                />
                                <img
                                  src="https://www.reussir-mon-ecommerce.fr/wp-content/uploads/2016/03/logo-visa-carte-ancien-1-300x165.png"
                                  alt="Visa"
                                  style={{ height: '30px', width: 'auto' }}
                                />
                                <img
                                  src="https://www.reussir-mon-ecommerce.fr/wp-content/uploads/2016/03/logo-american-express-1-300x300.png"
                                  alt="American Express"
                                  style={{ height: '30px', width: 'auto' }}
                                />
                              </HStack>
                            </HStack>
                            {isAlmaAvailable &&
                              customPaymentMethods?.map((method: PaymentMethod) => (
                                <HStack key={method.Id} alignItems="center">
                                  {isAlmaSelectable ? (
                                    <Radio value={method.Name} isDisabled={!isAlmaSelectable}>
                                      <HStack>
                                        <span>Paiement en plusieurs fois sans frais avec</span>
                                        <img
                                          src={`${import.meta.env.BASE_URL}logoalma.svg`}
                                          alt="Alma"
                                          style={{ height: '30px', width: 'auto', marginLeft: '0px' }}
                                        />
                                      </HStack>
                                    </Radio>
                                  ) : (
                                    <Tooltip label="Alma n'est pas autorisé car le total est inférieur à 50€">
                                      <Radio value={method.Name} isDisabled={!isAlmaSelectable}>
                                        <HStack>
                                          <span>Paiement en plusieurs fois sans frais avec</span>
                                          <img
                                            src={`${import.meta.env.BASE_URL}logoalma.svg`}
                                            alt="Alma"
                                            style={{ height: '30px', width: 'auto', marginLeft: '0px' }}
                                          />
                                        </HStack>
                                      </Radio>
                                    </Tooltip>
                                  )}
                                </HStack>
                              ))}
                          </Stack>
                        </RadioGroup>

                        {paymentMethod === 'Alma' ? (
                          <>
                            <Stack mt={0} pl={6} borderColor="gray.200">
                              {isLoadingPaymentPlans || isRefetchingPaymentPlans ? (
                                <Spinner size="lg" marginLeft="0" marginRight="auto" />
                              ) : (
                                <RadioGroup
                                  value={selectedPlan?.toString()}
                                  onChange={(value) => handlePlanSelect(parseInt(value))}
                                >
                                  <Stack direction="row" spacing={4}>
                                    {updatedPaymentPlans
                                      ?.filter((plan) => plan.installmentsCount > 1) // Exclude single installment plans
                                      .map((plan, index) => (
                                        <Box
                                          as="label"
                                          key={index}
                                          borderWidth="1px"
                                          borderRadius="md"
                                          cursor="pointer"
                                          bg={
                                            selectedPlan === plan.installmentsCount
                                              ? 'black !important'
                                              : 'white !important'
                                          }
                                          _focus={{
                                            boxShadow: 'outline',
                                          }}
                                          p="2px 10px"
                                          mb={0}
                                          textAlign="center"
                                        >
                                          <Radio value={plan.installmentsCount.toString()} display="none" />
                                          <Text
                                            fontWeight="bold"
                                            color={
                                              selectedPlan === plan.installmentsCount
                                                ? 'white !important'
                                                : 'gray.500 !important'
                                            }
                                            sx={{
                                              margin: 'inherit',
                                              display: 'flex',
                                              alignItems: 'center',
                                              height: '100%',
                                            }}
                                          >
                                            {plan.installmentsCount}x
                                          </Text>
                                        </Box>
                                      ))}
                                  </Stack>
                                </RadioGroup>
                              )}
                            </Stack>

                            {selectedPlan !== null && (
                              <>
                                <Text pl={6}>
                                  Paiement en <b>{selectedPlan} fois</b> sans frais par carte bancaire
                                </Text>
                                <Stack
                                  mt={4}
                                  maxW="700px"
                                  bg="#F5F5F5"
                                  p={4}
                                  borderRadius="md"
                                  className="checkoutInstallments"
                                >
                                  {updatedPaymentPlans
                                    ?.find((plan) => plan.installmentsCount === selectedPlan)
                                    ?.planDetails.map((detail) => (
                                      <HStack
                                        className="checkoutInstallment"
                                        key={detail.dueDate}
                                        justifyContent="space-between"
                                        w="full"
                                      >
                                        <Text className="checkoutInstallmentDate">
                                          {detail.localizedDueDate.toString().toLowerCase() === "aujourd'hui"
                                            ? "Aujourd'hui"
                                            : `Le ${detail.localizedDueDate}`}
                                        </Text>
                                        <Text>{`${detail.totalAmount} €`}</Text>
                                      </HStack>
                                    ))}
                                  <VStack alignItems="flex-end" mt="5" className="checkoutInstallmentsTotal">
                                    <Text fontWeight="bold" className="checkoutInstallmentsTotalAmount">
                                      Total : {`${totalAmount} €`}
                                    </Text>
                                    <Text color="gray.600" fontSize="sm" className="checkoutInstallmentsTotalFees">
                                      Les frais de paiement sont offerts 🎁
                                    </Text>
                                  </VStack>
                                </Stack>
                              </>
                            )}
                            <HStack sx={{ mt: 10 }}>
                              <FormControl>
                                <FormLabel>{t('Adresse e-mail')}</FormLabel>
                                <Input {...register('Email', { required: true })} />
                              </FormControl>
                              <FormControl>
                                <FormLabel>{t('Numéro de téléphone')}</FormLabel>
                                <PhoneInput
                                  international
                                  required
                                  inputComponent={Input}
                                  defaultCountry="FR"
                                  {...register('Phone', { required: true })}
                                  onChange={(value) => setValue('Phone', value ?? '')} // Provide a default empty string
                                />
                              </FormControl>
                            </HStack>
                            <HStack>
                              <FormControl>
                                <FormLabel>{t('Prénom')}</FormLabel>
                                <Input {...register('BillingName', { required: true })} />
                              </FormControl>
                              <FormControl>
                                <FormLabel>{t('Nom')}</FormLabel>
                                <Input {...register('BillingLastName', { required: true })} />
                              </FormControl>
                            </HStack>
                          </>
                        ) : (
                          <FormControl sx={{ mt: 10 }}>
                            <FormLabel>{t('Nom du titulaire')}</FormLabel>
                            <Input {...register('BillingName', { required: true })} />
                          </FormControl>
                        )}

                        {paymentMethod === 'Credit Card' ? (
                          <>
                            <Stack direction={{ base: 'column', md: 'row' }}>
                              <FormControl isInvalid={!!errors?.CreditCardNumber}>
                                <FormLabel>{t('Numéro de carte')}</FormLabel>
                                <Input
                                  {...register('CreditCardNumber', {
                                    required: 'Numéro de carte requis',
                                    validate: (value) => {
                                      const cleanedValue = value.replace(/\s/g, '');
                                      if (isAmex(cleanedValue)) {
                                        return cleanedValue.length === 15 || 'Numéro de carte AMEX invalide';
                                      }
                                      return cleanedValue.length === 16 || 'Numéro de carte invalide';
                                    },
                                  })}
                                  as={InputMask}
                                  mask={isAmex(creditCardNumber) ? '9999 999999 99999' : '9999 9999 9999 9999'}
                                  placeholder={isAmex(creditCardNumber) ? '1234 123456 12345' : '4242 1212 4242 1212'}
                                  maskChar={null}
                                />
                              </FormControl>

                              <HStack>
                                <FormControl isInvalid={!!errors?.Exp} minW={145}>
                                  <FormLabel>{t("Date d'expiration")}</FormLabel>
                                  <Input
                                    {...register('Exp', {
                                      required: "Date d'expiration requise",
                                      pattern: {
                                        value: /[0-1][0-9] \/ [0-9][0-9]/,
                                        message: "Date d'expiration invalide",
                                      },
                                    })}
                                    as={InputMask}
                                    placeholder="01 / 24"
                                    mask="99 / 99"
                                    maskChar={null}
                                  />
                                  <FormErrorMessage>{errors.Exp?.message}</FormErrorMessage>
                                </FormControl>
                                <FormControl isInvalid={!!errors?.Cryptogram}>
                                  <FormLabel>{t('Cryptogramme')}</FormLabel>
                                  <Input
                                    {...register('Cryptogram', {
                                      required: 'Cryptogramme requis',
                                      pattern: {
                                        value: isAmex(creditCardNumber) ? /^[0-9]{4}$/ : /^[0-9]{3}$/,
                                        message: 'Cryptogramme invalide',
                                      },
                                    })}
                                    as={InputMask}
                                    placeholder={isAmex(creditCardNumber) ? '1234' : '123'}
                                    mask={isAmex(creditCardNumber) ? '9999' : '999'}
                                    maskChar={null}
                                  />
                                </FormControl>
                              </HStack>
                            </Stack>
                            <Checkbox {...register('SaveInfo')}>{t('Enregistrer ce moyen de paiement')}</Checkbox>
                          </>
                        ) : null}
                      </Stack>
                    </Stack>
                  )}
                </Collapse>
              </>
            )}
          </>
        )}

        <Spacer mt={5} />

        {/* Message informatif sur SCA pour transactions nécessitant une authentification */}
        {paymentMethod === 'Credit Card' && remainingAmount >= 30 && (
          <Box
            className="scaInfoMessage"
            p={4}
            bg="blue.50"
            border="1px solid"
            borderColor="blue.200"
            borderRadius="md"
            mb={4}
            fontSize="sm"
            lineHeight="1.5"
          >
            <Text className="scaInfoText" fontWeight="medium" color="blue.800" mb={2}>
              Authentification 3D Secure {storedCreditCard === 'new' ? 'obligatoire' : 'requise'}
            </Text>
            <Text className="scaInfoText" color="blue.700">
              {storedCreditCard === 'new'
                ? "Pour votre sécurité, ce paiement nécessite une authentification 3D Secure par votre banque. Vous serez automatiquement redirigé(e) vers la page d'authentification."
                : "Pour votre sécurité, ce paiement peut nécessiter une authentification 3D Secure par votre banque. Vous serez automatiquement redirigé(e) vers la page d'authentification si nécessaire."}
            </Text>
          </Box>
        )}

        {/* Message spécial pour les produits lululemon */}
        {(Number(product.Id) === 113276 || Number(product.Id) === 113277 || Number(product.Id) === 113278) && (
          <Box
            className="lululemonMessage"
            p={4}
            bg="transparent"
            border="1px solid"
            borderColor="transparent"
            mb={4}
            fontSize="sm"
            lineHeight="1.5"
          >
            <Text className="lululemonText" textTransform="inherit">
              En achetant ce pack, vous acceptez de recevoir des e-mails promotionnels de la part de notre partenaire
              lululemon sur les produits, services, offres, histoires et événements conformément à la{' '}
              <Text
                className="lululemonLink"
                as="a"
                href="https://www.lululemon.fr/fr-fr/legal/politique-de-confidentialite/privacy.html"
                target="_blank"
                rel="noopener noreferrer"
                color="blue.500"
                textDecoration="underline"
                _hover={{ textDecoration: 'none' }}
              >
                politique de confidentialité
              </Text>{' '}
              de lululemon (vous pouvez vous désabonner via lululemon à tout moment).
            </Text>
          </Box>
        )}

        <Tooltip
          label={
            disableConfirmAlmaPayment
              ? "Vous avez déjà acheté une offre d'essai dans le passé. Ce type d'offre n'est disponible qu'une seule fois à la vente pour un client."
              : ''
          }
        >
          <Button
            ml="auto"
            type="submit"
            isLoading={paymentInProgress || isSubmitting}
            isDisabled={
              (remainingAmount === 0 ? false : !submitEnabled || disableConfirmAlmaPayment) ||
              isSubmitting ||
              paymentInProgress
            }
          >
            {isSubmitting
              ? paymentMethod === 'Alma'
                ? 'Préparation du paiement...'
                : 'Traitement du paiement...'
              : remainingAmount === 0
              ? t('Confirmer')
              : paymentMethod === 'Alma'
              ? t('Commencer le paiement')
              : t('Confirmer le paiement')}
          </Button>
        </Tooltip>
        <VStack spacing={1} alignItems="flex-end">
          <Text fontSize="xs" textAlign="right">
            Le crédit acheté peut mettre quelques instants avant de s'afficher dans votre compte.
          </Text>
          <Collapse in={isSubmitting}>
            <Text fontSize="sm" color="gray.600" fontStyle="italic">
              {paymentMethod === 'Alma'
                ? 'Veuillez patienter pendant que nous préparons votre paiement en plusieurs fois...'
                : 'Votre paiement est en cours de traitement, merci de ne pas actualiser la page...'}
            </Text>
          </Collapse>
          <Collapse in={showFallbackRedirect && paymentMethod === 'Credit Card'}>
            <Box mt={3} p={3} bg="orange.50" borderLeft="4px solid" borderLeftColor="orange.400" borderRadius="md">
              <Text fontSize="sm" color="orange.700" fontWeight="medium">
                ⚠️ Si la redirection ne se fait pas automatiquement,{' '}
                <Button
                  variant="link"
                  size="sm"
                  color="orange.600"
                  textDecoration="underline"
                  fontWeight="bold"
                  onClick={handleFallbackRedirect}
                  p={0}
                  h="auto"
                  minW="auto"
                  _hover={{ color: 'orange.800' }}
                >
                  cliquez ici pour accéder à votre compte
                </Button>
              </Text>
            </Box>
          </Collapse>
        </VStack>
      </Stack>
    </FormProvider>
  );
}

function CheckoutGiftCard() {
  const toast = useToast();
  const { setValue, watch } = useFormContext<CheckoutData>();
  const [isLoading, setIsLoading] = useState(false);

  async function checkGiftCardBalance() {
    const id = giftCardInputRef?.current?.value.trim();

    if (id) {
      setIsLoading(true);
      setValue('GiftCard', '');
      setValue('GiftCardBalance', undefined);
      const giftCardAmount = await HTTP.get<number>(`/sale/giftcard/${id}`)
        .send()
        .finally(() => setIsLoading(false));

      if (giftCardAmount === 0) {
        toast({
          title: `Le code "${id}" est invalide`,
          status: 'error',
        });

        giftCardInputRef.current?.focus();
      } else {
        setValue('GiftCard', id);
        setValue('GiftCardBalance', giftCardAmount);
      }
    }
  }

  const giftCardInputRef = useRef<HTMLInputElement | null>(null);
  const [giftCardAmount, amount] = watch(['GiftCardBalance', 'Amount']);

  return (
    <FormControl>
      <HStack className="GiftCardInput">
        <Input
          ref={giftCardInputRef}
          placeholder="Utiliser une carte cadeau"
          onKeyDown={(ev) => {
            if (ev.key === 'Enter') {
              ev.preventDefault();
              checkGiftCardBalance();
            }
          }}
        />
        <Button onClick={checkGiftCardBalance} isLoading={isLoading}>
          Appliquer
        </Button>
      </HStack>

      {giftCardAmount && giftCardAmount > 0 && (
        <FormHelperText>
          Cette carte cadeau à une valeur de {giftCardAmount} €, il restera{' '}
          {giftCardAmount - amount < 0 ? 0 : giftCardAmount - amount} € sur la carte cadeau après le paiement.
        </FormHelperText>
      )}
    </FormControl>
  );
}

function CheckoutPromoCode({ product }: { product: Product }) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const {
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext<CheckoutData>();

  function applyDiscount(promo: PromoCode) {
    const amount = getValues('Amount');

    if (promo.Discount.Type === 'Percent') {
      const discountedAmount = amount * (promo.Discount.Amount / 100);
      const totalAmount = amount - discountedAmount;
      setValue('TotalAmount', totalAmount);
      setValue('DiscountedAmount', discountedAmount);
    } else if (promo.Discount.Type === 'FlatRate') {
      const discountedAmount = promo.Discount.Amount;
      const totalAmount = amount - discountedAmount;
      setValue('TotalAmount', totalAmount);
      setValue('DiscountedAmount', discountedAmount);
    }
  }

  async function validatePromoCode() {
    const code = giftCardInputRef?.current?.value.trim();

    if (code) {
      setIsLoading(true);
      HTTP.post<PromoCode>('/sale/promocode', {
        code,
        productName: product.Name,
      })
        .send()
        .then((promoCode) => {
          if (!promoCode.AllowOnline) {
            return setError('PromoCode', { message: t("Ce code promo n'est pas utilisable en ligne") });
          }

          if (!promoCode.Active) {
            return setError('PromoCode', { message: t("Ce code promo n'est plus actif") });
          }

          // Logging pour débugger la validation du code promo
          console.log('🔍 Code promo validation debug:', {
            promoCode: promoCode.Code,
            productName: product.Name,
            productType: product.type,
            productProgramId: product.type === 'service' ? product.ProgramId : 'N/A',
            applicableItems: promoCode.ApplicableItems,
          });

          // Vérifier l'applicabilité du code promo
          const isApplicable =
            promoCode.ApplicableItems === null ||
            promoCode.ApplicableItems.some((item) => {
              // Pour les ServiceCategory, comparer avec ProgramId du produit
              if (item.Type === 'ServiceCategory' && product.type === 'service') {
                const matches = item.Id === product.ProgramId;
                console.log(
                  `  ✅ ServiceCategory check: ${item.Name} (ID: ${item.Id}) vs Product ProgramId: ${
                    product.ProgramId
                  } => ${matches ? 'MATCH' : 'NO MATCH'}`,
                );
                return matches;
              }
              // Fallback: vérifier si le nom du produit contient le nom de la catégorie
              const nameMatch = product.Name.toLowerCase().includes(item.Name.toLowerCase());
              console.log(
                `  📝 Name fallback check: "${product.Name}" includes "${item.Name}" => ${
                  nameMatch ? 'MATCH' : 'NO MATCH'
                }`,
              );
              return nameMatch;
            });

          console.log(
            `🎯 Final result: Code promo "${promoCode.Code}" is ${
              isApplicable ? 'APPLICABLE' : 'NOT APPLICABLE'
            } to product "${product.Name}"`,
          );

          if (!isApplicable) {
            return setError('PromoCode', {
              message: t("Ce code promo n'est pas applicable à ce produit"),
            });
          }

          setValue('PromoCode', promoCode.Code);
          applyDiscount(promoCode);
        })
        .catch((error: ApiClientException) => {
          setError('PromoCode', {
            message: t('Code promo invalide'),
          });
          console.log('error', error);
        })
        .finally(() => setIsLoading(false));
    }
  }

  const giftCardInputRef = useRef<HTMLInputElement | null>(null);

  return (
    <FormControl isInvalid={!!errors.PromoCode}>
      <HStack className="PromoCodeInput">
        <Input
          placeholder="Utiliser un code promo"
          ref={giftCardInputRef}
          onFocus={() => clearErrors('PromoCode')}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter') {
              ev.preventDefault();
              validatePromoCode();
            }
          }}
        />
        <Button isLoading={isLoading} onClick={validatePromoCode}>
          Appliquer
        </Button>
      </HStack>

      <FormErrorMessage>{errors.PromoCode?.message}</FormErrorMessage>
    </FormControl>
  );
}
